<template>
  <div class="irp-master-tab" data-testid="irp-master-tab">
    <h3 class="irp-master-tab__heading">{{ detailsHeading }}</h3>
    <IRPFieldGrid :fields="ownAccountFields" />

    <IRPPartnerDataSection
      :key="individualRecordStore.recordKey"
      section="master"
    >
      <template #default="{ partner }">
        <IRPFieldGrid :fields="partnerAccountFields(partner)" />
      </template>
    </IRPPartnerDataSection>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useIndividualRecordStore } from '@/stores';
import type {
  RecordCollectionType,
  RecordFieldType,
  RecordPartnerType,
} from '@/types/individual_record';

import IRPFieldGrid from './IRPFieldGrid.vue';
import IRPPartnerDataSection from './IRPPartnerDataSection.vue';

const individualRecordStore = useIndividualRecordStore();

const detailsHeading = computed(() =>
  individualRecordStore.mdmType === 'lead' ? 'Lead details' : 'Account details',
);

function mergeWithOwners(
  fields: RecordFieldType[],
  accountOwners: RecordCollectionType | undefined,
): RecordFieldType[] {
  const ownerFields = (accountOwners?.items ?? []).flatMap(
    (owner) => owner.fields,
  );
  return [...fields, ...ownerFields];
}

const ownAccountFields = computed<RecordFieldType[]>(() =>
  mergeWithOwners(
    individualRecordStore.master?.own?.fields ?? [],
    individualRecordStore.master?.own?.account_owners as
      | RecordCollectionType
      | undefined,
  ),
);

function partnerAccountFields(partner: RecordPartnerType<'master'>) {
  return mergeWithOwners(
    partner.fields,
    partner.account_owners as RecordCollectionType | undefined,
  );
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-master-tab {
  @apply flex flex-col gap-16;
}

.irp-master-tab__heading {
  @apply text-base font-bold text-neutral-text-strong;
}
</style>
